import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminUnitLevel } from "@/modules/chart/admin/types";

export type UnitLevelList = Paginate<AdminUnitLevel<true, true>>;

export const unitLevels = createQueryKeys("admin-chart-unit-levels", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "users,parent";

export const fetchUnitLevelsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...unitLevels.all(page, search),
    queryFn: () =>
      adminApi
        .get<UnitLevelList>(`/chart/units/levels`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchUnitLevels = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchUnitLevelsQueryOptions(page, search));

export const fetchUnitLevelQueryOptions = (id?: number) =>
  queryOptions({
    ...unitLevels.one(id),
    queryFn: () =>
      adminApi
        .get<AdminUnitLevel<true, true>>(`/chart/units/levels/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchUnitLevel = (id?: number) =>
  useQuery(fetchUnitLevelQueryOptions(id));

export interface UnitLevelRequest {
  name: string;
  type: "official" | "operational";
  parent_id?: number | null;
}

export const useCreateUnitLevel = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UnitLevelRequest) =>
      adminApi
        .post("/chart/units/levels", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: unitLevels.all._def,
      });
    },
  });
};

export const useUpdateUnitLevel = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UnitLevelRequest) =>
      adminApi
        .put(`/chart/units/levels/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: unitLevels.all._def,
      });
      queryClient.invalidateQueries(unitLevels.one(id));
    },
  });
};

export const useCreateOrUpdateUnitLevel = (id: number | undefined) => {
  const create = useCreateUnitLevel();
  const update = useUpdateUnitLevel(id!);
  return id ? update : create;
};

export const useDeleteUnitLevel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`chart/units/levels/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: unitLevels.all._def,
      });
      queryClient.invalidateQueries(unitLevels.one(id));
    },
  });
};
